import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import {handleErrorResponse} from "../utils/util"
import { USER_ROLE } from "../constants/index"
import { TagService } from "../api/services.gen";
import {
    TagTalentTagListData, TagTalentTagCreateData, TagTalentTagRetrieveData
} from "../api/types.gen";
import {INTERNAL_SERVER_ERROR} from "../constants/index";
import i18n from 'i18next';
import { ceil } from "lodash";
import { resetAppState } from "../store/common";

export const getTalentTags = createAsyncThunk(
    "getTalentTags",
    async (data: TagTalentTagListData, thunkAPI: any) => {
        try {
            const res: any = await TagService.tagTalentTagList(data);
            if (res.results.success) {
                return res;
            } else {
                if(res.message) return thunkAPI.rejectWithValue(res.message);
                else return thunkAPI.rejectWithValue(i18n.t('MSG_GLOBAL_DATA_LOAD_FAILED'));
            }
        } catch (error: any) {
            return handleErrorResponse(error, USER_ROLE.COMPANY, thunkAPI);
        }
    },
);

export const createTalentTag = createAsyncThunk(
    "createTalentTag",
    async (data: TagTalentTagCreateData, thunkAPI: any) => {
        try {
            const res: any = await TagService.tagTalentTagCreate(data);
            if (res.success) {
                return res;
            } else {
                if(res.message) return thunkAPI.rejectWithValue(res.message);
                else return thunkAPI.rejectWithValue(i18n.t('MSG_GLOBAL_DATA_LOAD_FAILED'));
            }
        } catch (error: any) {
            return handleErrorResponse(error, USER_ROLE.COMPANY, thunkAPI);
        }
    },
);

export const getTalentTagDetail = createAsyncThunk(
    "getTalentTagDetail",
    async (data: TagTalentTagRetrieveData, thunkAPI: any) => {
        try {
            const res: any = await TagService.tagTalentTagRetrieve(data);
            if (res.success) {
                return res;
            } else {
                if(res.message) return thunkAPI.rejectWithValue(res.message);
                else return thunkAPI.rejectWithValue(i18n.t('MSG_GLOBAL_DATA_LOAD_FAILED'));
            }
        } catch (error: any) {
            return handleErrorResponse(error, USER_ROLE.COMPANY, thunkAPI);
        }
    },
);

export const updateTalentTag = createAsyncThunk(
    "updateTalentTag",
    async (data: TagTalentTagUpdateData, thunkAPI: any) => {
        try {
            const res: any = await TagService.tagTalentTagUpdate(data);
            if (res.success) {
                return res;
            } else {
                if(res.message) return thunkAPI.rejectWithValue(res.message);
                else return thunkAPI.rejectWithValue(i18n.t('MSG_GLOBAL_DATA_LOAD_FAILED'));
            }
        } catch (error: any) {
            return handleErrorResponse(error, USER_ROLE.COMPANY, thunkAPI);
        }
    },
);

export const deleteTalentTag = createAsyncThunk(
    "deleteTalentTag",
    async (data: TagTalentTagDestroyData, thunkAPI: any) => {
        try {
            const res: any = await TagService.tagTalentTagDestroy(data);
            if (res.success) {
                return res;
            } else {
                if(res.message) return thunkAPI.rejectWithValue(res.message);
                else return thunkAPI.rejectWithValue(i18n.t('MSG_GLOBAL_DATA_LOAD_FAILED'));
            }
        } catch (error: any) {
            return handleErrorResponse(error, USER_ROLE.COMPANY, thunkAPI);
        }
    },
);


const initTableHeaders = [
    {
        title: "タグ名",
        isSort: true,
        field: "title",
        currentSortState: 1,
        width: "w-[288px]"
    },
    {
        title: "詳細",
        isSort: false,
        field: "description",
        currentSortState: 0,
    },
    {
        title: "タレント数",
        isSort: true,
        field: "number_of_talents",
        currentSortState: 0,
        width: "w-[120px]"
    },
    {
        title: "",
        isSort: false,
        width: "w-[194px]"
    },
]

// Initial state of Redux
interface SuperUserState {
    loading: boolean;
    error: string | null;
    success: string | null;
    actionName: string | null;
    actionStatus: string;
    countItems: number;
    currentPage: number;
    pageSize: number;
    sortBy: string | null;
    sortOrder: string | null;
    searchQuery: string | null;
    tableHeaders: TableHeader[];
    detailData: [];
    talentTagsList: {};
    isReload: boolean;
    isFromTagsList: boolean;
    isOpenSearchTagsPanel: boolean;
}

const initialState: SuperUserState = {
    loading: false,
    error: null,
    success: null,
    actionName: null,
    actionStatus: "idle", // 'idle' | 'loading' | 'succeeded' | 'failed'
    countItems: 0,
    currentPage: 1,
    pageSize: 20,
    sortBy: "title",
    sortOrder: "asc",
    searchQuery: "",
    tableHeaders: initTableHeaders,
    talentDetailData: [],
    talentTagsList: {},
    isReload: false,
    isFromTagsList: false,
    isOpenSearchTagsPanel: false,
};

const sortingStates = [ "default", "asc", "desc"];

export const talentTagSlice = createSlice({
    name: "talentTag",
    initialState,
    reducers: {
        setSuccess: (state, action) => {
            state.success = action.payload;
        },
        setError: (state, action) => {
            state.error = action.payload;
        },
        setCurrentPage: (state, action) => {
            state.currentPage = action.payload;
        },
        setSearchQuery: (state, action) => {
            state.searchQuery = action.payload;
        },
        handleSortByHeader(state, action) {
            const sortHeader = action.payload;
            state.tableHeaders = state.tableHeaders.map((header, idx) => (header.field === sortHeader.field)
            ? ({...header, currentSortState: (header.currentSortState + 1) % sortingStates.length})
            : ({...header, currentSortState: 0}));
            
            if((sortHeader.currentSortState + 1) % sortingStates.length !== 0) {
                state.sortBy = sortHeader.field;
                state.sortOrder = sortingStates[(sortHeader.currentSortState + 1) % sortingStates.length];
            } else {
                state.sortBy = null;
                state.sortOrder = null;
            }
        },
        setTalentTagsList: (state, action) => {
            state.talentTagsList = action.payload;
        },
        setTalentDetailData: (state, action) => {
            state.talentDetailData = action.payload;
        },
        setIsFromTagsList: (state, action) => {
            state.isFromTagsList = action.payload;
        },
        setIsOpenSearchTagsPanel: (state, action) => {
            state.isOpenSearchTagsPanel = action.payload;
        },
    },
    extraReducers: (builder) => {
        builder
        // reset state
        .addCase(resetAppState, () => initialState)

        // getTalentTags
        .addCase(getTalentTags.pending, (state) => {
            state.loading = true;
            state.error = null;
        })
        .addCase(getTalentTags.fulfilled, (state, action) => {
            state.loading = false;
            state.talentTagsList = action.payload.results?.data;
            state.countItems = action.payload.count;
        })
        .addCase(getTalentTags.rejected, (state, action) => {
            state.loading = false;
            state.error = action.payload as string;
        })

        // createTalentTag
        .addCase(createTalentTag.pending, (state) => {
            state.loading = true;
            state.error = null;
        })
        .addCase(createTalentTag.fulfilled, (state, action) => {
            state.loading = false;
            state.success = action.payload?.message || "";
            
            const newItem = action.payload?.data;
            const oldList = Array.isArray(state.talentTagsList) ? state.talentTagsList : [];
            state.talentTagsList = newItem ? [newItem, ...oldList] : oldList;
            if (state.pageSize && state.currentPage < ceil(parseInt(state.countItems) / parseInt(state.pageSize))) {
                state.talentTagsList = state.talentTagsList.slice(0, state.pageSize);
            }
        })
        .addCase(createTalentTag.rejected, (state, action) => {
            state.loading = false;
            state.error = action.payload as string;
        })

        // getTalentTagDetail
        .addCase(getTalentTagDetail.pending, (state) => {
            state.loading = true;
            state.error = null;
        })
        .addCase(getTalentTagDetail.fulfilled, (state, action) => {
            state.loading = false;
            state.success = action.payload.message;
            state.talentDetailData = action.payload.data;
        })
        .addCase(getTalentTagDetail.rejected, (state, action) => {
            state.loading = false;
            state.error = action.payload as string;
        })

        // updateTalentTag
        .addCase(updateTalentTag.pending, (state) => {
            state.loading = true;
            state.error = null;
        })
        .addCase(updateTalentTag.fulfilled, (state, action) => {
            state.loading = false;
            state.success = action.payload?.message || "";

            const updatedTag = action.payload.data;
            const index = state.talentTagsList.findIndex(
                (item: any) => item.id === updatedTag.id
            );

            if (index !== -1) {
                state.talentTagsList[index] = {
                    ...state.talentTagsList[index],
                    ...updatedTag,
                };
            }
        })
        .addCase(updateTalentTag.rejected, (state, action) => {
            state.loading = false;
            state.error = action.payload as string;
        })

        // deleteTalentTag
        .addCase(deleteTalentTag.pending, (state) => {
            state.loading = true;
            state.error = null;
        })
        .addCase(deleteTalentTag.fulfilled, (state, action) => {
            state.loading = false;
            state.success = action.payload.message;
            
            if (parseInt(state.talentTagsList.length) - 1 == 0) {
                state.currentPage = state.currentPage > 1 ? state.currentPage - 1 : 1;
            }
            state.isReload = !state.isReload;
        })
        .addCase(deleteTalentTag.rejected, (state, action) => {
            state.loading = false;
            state.error = action.payload as string;
        })
    }
});
export const {
    setSuccess,
    setError,
    setCurrentPage,
    setSearchQuery,
    handleSortByHeader,
    setTalentTagsList,
    setTalentDetailData,
    setIsFromTagsList,
    setIsOpenSearchTagsPanel,
} = talentTagSlice.actions;
export default talentTagSlice.reducer;